<script setup lang="ts">
import {
  ArrowLeftRight as ArrowLeftRightIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Loader2 as Loader2Icon,
  Redo as RedoIcon,
  Undo as UndoIcon,
} from '@lucide/vue'
import { ref } from 'vue'

import RoutePointList from '@/components/map/toolbar/RoutePointList.vue'
import BaseSegmentedControl from '@/components/ui/BaseSegmentedControl.vue'
import BaseToolbox from '@/components/ui/BaseToolbox.vue'
import { ROUTING_PROFILES } from '@/services/routingService'
import { useUiStore } from '@/stores/uiStore'
import type { Tour } from '@/types/tour'
import type { TrackProfileMetricId } from '@/utils/trackProfileAnalysis'

const props = withDefaults(
  defineProps<{
    canReverse: boolean
    canUndo: boolean
    canRedo: boolean
    canSave: boolean
    isCalculatingRoute: boolean
    isBusy?: boolean
    statusText?: string | null
    profileMetric?: TrackProfileMetricId
    /** Tour being edited — drives the "Your Route" route-point list. */
    tour?: Tour | null
    /** Track being edited, to scope the list to it. */
    trackClientId?: string | null
  }>(),
  {
    isBusy: false,
    statusText: null,
    profileMetric: 'gradient',
    tour: null,
    trackClientId: null,
  },
)

const emit = defineEmits<{
  (e: 'reverse'): void
  (e: 'undo'): void
  (e: 'redo'): void
  (e: 'save'): void
  (e: 'cancel'): void
  (e: 'routing-profile-change', profile: string): void
  (e: 'profile-metric-change', metric: TrackProfileMetricId): void
  (e: 'zoom-route-point', tourId: string, routePointIndex: number): void
  (
    e: 'reorder-route-points',
    tourId: string,
    trackClientId: string | null,
    orderedRoutePointClientIds: string[],
  ): void
}>()

const uiStore = useUiStore()
const isCollapsed = ref(false)
const isInteractionDisabled = () => props.isBusy || props.isCalculatingRoute

const profileMetricOptions: Array<{ value: TrackProfileMetricId; label: string }> = [
  { value: 'gradient', label: 'Gradient' },
  { value: 'surface', label: 'Surface' },
  { value: 'wayType', label: 'Way type' },
]
</script>

<template>
  <BaseToolbox
    :is-open="true"
    title="Track routing"
    :is-collapsed="isCollapsed"
    :collapsible="true"
    :closeable="false"
    elevated
    :initial-offset-y="240"
    panel-class="w-[420px] animate-in slide-in-from-bottom-4 duration-200"
    body-class="space-y-3"
    data-testid="track-routing-toolbox"
    @update:is-collapsed="isCollapsed = $event"
  >
    <div class="text-sm font-medium text-gray-700 text-center">
      Click map to
      {{ uiStore.editingTrackAppendDirection === 'end' ? 'extend' : 'prepend to' }} track
    </div>

    <!-- "Your Route" — manage anchors from a list (also reachable when markers sit behind the panel). -->
    <div
      v-if="tour"
      class="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/60"
    >
      <RoutePointList
        :tour="tour"
        :track-client-id="trackClientId"
        :expanded="true"
        :can-delete-route-points="true"
        :reorderable="true"
        :hide-track-headers="true"
        size="comfortable"
        @zoom-route-point="(tourId, index) => emit('zoom-route-point', tourId, index)"
        @reorder-route-points="
          (tourId, trackClientId, ids) => emit('reorder-route-points', tourId, trackClientId, ids)
        "
      />
    </div>

    <div class="grid w-full gap-2">
      <select
        :value="uiStore.editingTrackAppendDirection"
        @change="
          uiStore.setEditingTrackAppendDirection(
            ($event.target as HTMLSelectElement).value as 'end' | 'start',
          )
        "
        :disabled="isInteractionDisabled()"
        class="w-full min-w-0 text-xs px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
        title="Extend direction"
      >
        <option value="end">Append to end</option>
        <option value="start">Prepend to start</option>
      </select>

      <select
        :value="uiStore.routingProfile"
        @change="emit('routing-profile-change', ($event.target as HTMLSelectElement).value)"
        :disabled="isInteractionDisabled()"
        class="w-full min-w-0 text-xs px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
        title="Routing profile"
      >
        <option v-for="p in ROUTING_PROFILES" :key="p.id" :value="p.id">{{ p.label }}</option>
      </select>

      <BaseSegmentedControl
        variant="compact"
        label="Color by"
        aria-label="Color route by"
        :model-value="props.profileMetric"
        :options="profileMetricOptions"
        :disabled="isInteractionDisabled()"
        @update:model-value="emit('profile-metric-change', $event as TrackProfileMetricId)"
      />
    </div>

    <div
      v-if="(isBusy || isCalculatingRoute) && statusText"
      class="flex items-center justify-center gap-2 rounded-md bg-primary-50 px-3 py-2 text-xs font-medium text-primary-700"
    >
      <Loader2Icon class="w-4 h-4 animate-spin" />
      <span>{{ statusText }}</span>
    </div>

    <div class="flex flex-wrap items-center justify-center gap-2">
      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
        :disabled="!canReverse || isInteractionDisabled()"
        title="Reverse route"
        @click="emit('reverse')"
      >
        <ArrowLeftRightIcon class="w-4 h-4" />
        <span>Reverse route</span>
      </button>

      <button
        class="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canUndo || isInteractionDisabled()"
        title="Undo last route edit"
        @click="emit('undo')"
      >
        <UndoIcon class="w-4 h-4" />
      </button>

      <button
        class="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canRedo || isInteractionDisabled()"
        title="Redo route edit"
        @click="emit('redo')"
      >
        <RedoIcon class="w-4 h-4" />
      </button>

      <button
        :class="[
          'p-2 rounded-md transition-colors hover:cursor-pointer',
          uiStore.isMapElementsHiddenByButton
            ? 'bg-gray-800 text-white hover:bg-gray-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        ]"
        :disabled="isInteractionDisabled()"
        :title="
          uiStore.isMapElementsHiddenByButton ? 'Show map elements (M)' : 'Hide map elements (M)'
        "
        @click="uiStore.toggleMapElementsHiddenByButton()"
      >
        <EyeOffIcon v-if="uiStore.isMapElementsHiddenByButton" class="w-4 h-4" />
        <EyeIcon v-else class="w-4 h-4" />
      </button>
    </div>

    <div class="flex w-full items-center justify-center gap-3 pt-1">
      <button
        class="min-w-[120px] px-4 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-50 transition-colors hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isInteractionDisabled()"
        @click="emit('cancel')"
      >
        Cancel
      </button>

      <button
        class="min-w-[120px] px-4 py-1.5 rounded-md bg-primary-600 text-white text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canSave || isInteractionDisabled()"
        @click="emit('save')"
      >
        Save
      </button>
    </div>
  </BaseToolbox>
</template>
